import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Slider,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Collapse,
  Popover,
  Paper,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  SkipPrevious as BackIcon,
  SkipNext as ForwardIcon,
  Fullscreen as FullExtentIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { PathfinderDataPoint, PathfinderRoute } from '../../services/pathfinderApi';

interface CompactTimeSliderProps {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  timeRange: { start: Date; end: Date } | null;
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  dwellLocations?: any[]; // Optional dwell data for skip dwells feature
  onTimeUpdate: (time: Date, visiblePoints: PathfinderDataPoint[], visibleRoutes: PathfinderRoute[]) => void;
  onFollowModeChange?: (enabled: boolean) => void;
  // Visibility controls
  showDataPoints?: boolean;
  onShowDataPointsChange?: (show: boolean) => void;
  showRouteLines?: boolean;
  onShowRouteLinesChange?: (show: boolean) => void;
  showDirectionArrows?: boolean;
  onShowDirectionArrowsChange?: (show: boolean) => void;
  showHeatmap?: boolean;
  onShowHeatmapChange?: (show: boolean) => void;
  enableClustering?: boolean;
  onEnableClusteringChange?: (enable: boolean) => void;
  showDwells?: boolean;
  onShowDwellsChange?: (show: boolean) => void;
  totalDataPoints?: number;
  visibleDataPointsCount?: number;
}

const timeUnits = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const intervals = Array.from({ length: 60 }, (_, i) => i + 1);
const playRates = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.5, 2.0, 2.5, 3.0];

const CompactTimeSlider: React.FC<CompactTimeSliderProps> = ({
  dataPoints,
  routes,
  timeRange,
  selectedDevices,
  dwellLocations = [],
  onTimeUpdate,
  onFollowModeChange,
  // Visibility controls
  showDataPoints = true,
  onShowDataPointsChange,
  showRouteLines = false,
  onShowRouteLinesChange,
  showDirectionArrows = false,
  onShowDirectionArrowsChange,
  showHeatmap = false,
  onShowHeatmapChange,
  enableClustering = true,
  onEnableClusteringChange,
  showDwells = false,
  onShowDwellsChange,
  totalDataPoints = 0,
  visibleDataPointsCount = 0,
}) => {
  // Theme hook for consistent colors
  const theme = useTheme();
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeExtent, setTimeExtent] = useState<[number, number]>([0, 100]); // Percentage of total time [start%, end%]
  const [fullTimeExtent, setFullTimeExtent] = useState<{ start: Date; end: Date } | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showFullExtent, setShowFullExtent] = useState(true); // Start with FULL EXTENT to show all data initially
  const [visibilityAnchorEl, setVisibilityAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  // Playback settings
  const [timeUnit, setTimeUnit] = useState('seconds');
  const [intervalValue, setIntervalValue] = useState(30);
  const [playRate, setPlayRate] = useState(0.5);
  const [loop, setLoop] = useState(true);
  const [followMode, setFollowMode] = useState(false);
  const [skipDwells, setSkipDwells] = useState(false);
  
  const playbackTimerRef = useRef<number | null>(null);
  const windowSizeRef = useRef<number>(5); // Store window size as percentage (default ~5%)

  // Initialize time extent when data loads (ONLY on initial data load)
  useEffect(() => {
    if (timeRange) {
      setFullTimeExtent(timeRange);
      
      // Calculate initial window size based on interval + unit
      const totalDurationMs = timeRange.end.getTime() - timeRange.start.getTime();
      const intervalMs = intervalValue * (
        timeUnit === 'seconds' ? 1000 : 
        timeUnit === 'minutes' ? 60000 : 
        timeUnit === 'hours' ? 3600000 : 86400000
      );
      const windowSizePercent = Math.min(100, (intervalMs / totalDurationMs) * 100);
      windowSizeRef.current = windowSizePercent;
      
      // Start with full extent (ALL) to show all data initially
      // When showFullExtent is true, the slider should span 0-100%
      setTimeExtent([0, 100]);
    }
  }, [timeRange, intervalValue, timeUnit]); // Only depend on timeRange, not interval settings

  // Update window size when interval settings change - apply immediately if in window mode
  useEffect(() => {
    if (fullTimeExtent && !showFullExtent) {
      const totalDurationMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
      const intervalMs = intervalValue * (
        timeUnit === 'seconds' ? 1000 : 
        timeUnit === 'minutes' ? 60000 : 
        timeUnit === 'hours' ? 3600000 : 86400000
      );
      const windowSizePercent = Math.min(100, (intervalMs / totalDurationMs) * 100);
      windowSizeRef.current = windowSizePercent;
      
      // Apply immediately if in window mode (not full extent)
      setTimeExtent(prev => {
        const currentCenter = (prev[0] + prev[1]) / 2;
        let newStart = Math.max(0, currentCenter - windowSizePercent / 2);
        let newEnd = Math.min(100, currentCenter + windowSizePercent / 2);
        
        // Adjust if we hit boundaries
        if (newStart === 0) newEnd = Math.min(100, windowSizePercent);
        if (newEnd === 100) newStart = Math.max(0, 100 - windowSizePercent);
        
        return [newStart, newEnd];
      });
    } else if (fullTimeExtent) {
      // Just update reference if in full extent mode
      const totalDurationMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
      const intervalMs = intervalValue * (
        timeUnit === 'seconds' ? 1000 : 
        timeUnit === 'minutes' ? 60000 : 
        timeUnit === 'hours' ? 3600000 : 86400000
      );
      const windowSizePercent = Math.min(100, (intervalMs / totalDurationMs) * 100);
      windowSizeRef.current = windowSizePercent;
    }
  }, [intervalValue, timeUnit, fullTimeExtent, showFullExtent]);

  // Convert percentage to date
  const getDateFromPercentage = useCallback((percentage: number): Date => {
    if (!fullTimeExtent) return new Date();
    const totalMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
    return new Date(fullTimeExtent.start.getTime() + (totalMs * percentage) / 100);
  }, [fullTimeExtent]);

  // Get visible data for current time window
  const getVisibleDataClientSide = useCallback(() => {
    if (!fullTimeExtent) return { points: [], routes: [] };

    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    // Determine the visible time window from timeExtent percentages
    const windowStart = getDateFromPercentage(timeExtent[0]);
    const windowEnd = getDateFromPercentage(timeExtent[1]);
    
    // Removed verbose logging - data filtering is working correctly
    
    const visiblePoints = dataPoints.filter(point => {
      // When showing full extent, skip time filtering
      if (!showFullExtent) {
        const pointTime = point.timestamp.getTime();
        const inWindow = pointTime >= windowStart.getTime() && pointTime <= windowEnd.getTime();
        if (!inWindow) return false;
      }
      
      // Always apply device filtering
      if (selectedDeviceIds.length === 0) return true;
      // Use the human-readable format: "Student-XX-devicetype"
      const deviceKey = `${point.studentId}-${(point.deviceType || 'phone').toLowerCase()}`;
      return selectedDeviceIds.includes(deviceKey);
    });

    // Filter routes by time - create progressive routes that only show points within the time window
    const visibleRoutes = routes
      .map(route => {
        // First check device selection
        if (selectedDeviceIds.length > 0 && !selectedDeviceIds.includes(route.deviceId)) {
          return null;
        }
        
        // When showing full extent, show all route points
        if (showFullExtent) {
          return route; // Return entire route without time filtering
        }
        
        // Find all points within the time window
        const pointsInWindow = route.points.filter(point => {
          const pointTime = point.timestamp.getTime();
          return pointTime >= windowStart.getTime() && pointTime <= windowEnd.getTime();
        });
        
        // If no points in window, return null
        if (pointsInWindow.length === 0) {
          return null;
        }
        
        // Sort points by timestamp (ensure proper line drawing order)
        pointsInWindow.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        
        // Create segmented route with updated coordinates array
        return {
          ...route,
          points: pointsInWindow,
          coordinates: pointsInWindow.map(p => p.coordinates), // Important: update coordinates for line rendering
          startTime: pointsInWindow[0].timestamp,
          endTime: pointsInWindow[pointsInWindow.length - 1].timestamp
        };
      })
      .filter((route): route is PathfinderRoute => route !== null); // Remove null routes

    
    return { points: visiblePoints, routes: visibleRoutes };
  }, [dataPoints, routes, fullTimeExtent, selectedDevices, timeExtent, showFullExtent, getDateFromPercentage]);

  // Handle time update
  const scheduleOnTimeUpdate = useCallback((time: Date, points: PathfinderDataPoint[], routes: PathfinderRoute[]) => {
    onTimeUpdate(time, points, routes);
  }, [onTimeUpdate]);

  // Helper function to check if current time is within a dwell period
  const isInDwell = useCallback((currentTimeMs: number): { inDwell: boolean; dwellEnd?: number } => {
    if (!skipDwells || dwellLocations.length === 0) {
      return { inDwell: false };
    }
    
    for (const dwell of dwellLocations) {
      const dwellStart = new Date(dwell.start_time).getTime();
      const dwellEnd = new Date(dwell.end_time).getTime();
      
      if (currentTimeMs >= dwellStart && currentTimeMs <= dwellEnd) {
        return { inDwell: true, dwellEnd };
      }
    }
    
    return { inDwell: false };
  }, [skipDwells, dwellLocations]);

  // Playback logic - advances the time window forward, skipping dwells if enabled
  useEffect(() => {
    if (isPlaying && fullTimeExtent) {
      const timerId = window.setInterval(() => {
        setTimeExtent(prev => {
          const windowSize = prev[1] - prev[0];
          const totalDurationMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
          
          // Calculate how much to advance (as percentage) based on interval
          const intervalMs = intervalValue * (
            timeUnit === 'seconds' ? 1000 : 
            timeUnit === 'minutes' ? 60000 : 
            timeUnit === 'hours' ? 3600000 : 86400000
          );
          let advancePercent = (intervalMs / totalDurationMs) * 100;
          
          let newStart = prev[0] + advancePercent;
          let newEnd = prev[1] + advancePercent;
          
          // If skip dwells is enabled, check if we're in a dwell and skip to the end of it
          if (skipDwells && dwellLocations.length > 0) {
            const currentTime = getDateFromPercentage((newStart + newEnd) / 2);
            const dwellCheck = isInDwell(currentTime.getTime());
            
            if (dwellCheck.inDwell && dwellCheck.dwellEnd) {
              // Skip to the end of the dwell
              const dwellEndPercent = ((dwellCheck.dwellEnd - fullTimeExtent.start.getTime()) / totalDurationMs) * 100;
              const skipAmount = dwellEndPercent - ((prev[0] + prev[1]) / 2);
              
              if (skipAmount > 0) {
                newStart = prev[0] + skipAmount;
                newEnd = prev[1] + skipAmount;
              }
            }
          }
          
          // Check if we've reached the end
          if (newEnd >= 100) {
            if (loop) {
              // Loop back to start with same window size
              return [0, windowSize];
            } else {
              setIsPlaying(false);
              return [100 - windowSize, 100];
            }
          }
          
          return [newStart, newEnd];
        });
      }, playRate * 1000);
      
      playbackTimerRef.current = timerId;

      return () => {
        if (playbackTimerRef.current) {
          clearInterval(playbackTimerRef.current);
        }
      };
    }
  }, [isPlaying, intervalValue, timeUnit, playRate, loop, fullTimeExtent, skipDwells, dwellLocations, isInDwell, getDateFromPercentage]);

  // Update visible data when time extent changes
  useEffect(() => {
    if (fullTimeExtent) {
      const visibleData = getVisibleDataClientSide();
      const currentTime = getDateFromPercentage((timeExtent[0] + timeExtent[1]) / 2); // Middle of window
      scheduleOnTimeUpdate(currentTime, visibleData.points, visibleData.routes);
    }
  }, [timeExtent, getVisibleDataClientSide, scheduleOnTimeUpdate, fullTimeExtent, getDateFromPercentage]);

  // Handle slider change - update the time window
  const handleSliderChange = useCallback((_: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue) && newValue.length === 2) {
      setTimeExtent([newValue[0], newValue[1]]);
      setIsPlaying(false);
      
      // Update window size ref
      windowSizeRef.current = newValue[1] - newValue[0];
      
      // If user manually adjusts slider, exit full extent mode
      if (showFullExtent) {
        setShowFullExtent(false);
      }
    }
  }, [showFullExtent]);

  const handleFollowModeChange = useCallback((enabled: boolean) => {
    setFollowMode(enabled);
    if (onFollowModeChange) {
      onFollowModeChange(enabled);
    }
  }, [onFollowModeChange]);

  // Handler to adjust window size (widen/narrow the visible time span)
  const adjustWindowSize = useCallback((direction: 'narrower' | 'wider') => {
    setTimeExtent(prev => {
      const currentSize = prev[1] - prev[0];
      const center = (prev[0] + prev[1]) / 2;
      
      // Adjust by 10% increments
      const adjustment = direction === 'wider' ? currentSize * 0.1 : currentSize * -0.1;
      let newSize = Math.max(1, Math.min(100, currentSize + adjustment)); // Min 1%, max 100%
      
      // Calculate new start and end, keeping center position
      let newStart = Math.max(0, center - newSize / 2);
      let newEnd = Math.min(100, center + newSize / 2);
      
      // Adjust if we hit boundaries
      if (newStart === 0) newEnd = Math.min(100, newSize);
      if (newEnd === 100) newStart = Math.max(0, 100 - newSize);
      
      windowSizeRef.current = newEnd - newStart;
      return [newStart, newEnd];
    });
    setShowFullExtent(false);
    setIsPlaying(false);
  }, []);

  // Handler to toggle full extent
  const toggleFullExtent = useCallback(() => {
    if (!showFullExtent) {
      // Switch TO full extent - save current window position first
      
      // Save current window size (in case user manually adjusted slider)
      const currentWindowSize = timeExtent[1] - timeExtent[0];
      if (currentWindowSize < 100) {
        windowSizeRef.current = currentWindowSize;
      }
      
      setTimeExtent([0, 100]);
      setShowFullExtent(true);
      setIsPlaying(false);
    } else {
      // Switch FROM full extent back to window mode
      const windowSize = windowSizeRef.current;
      
      // If windowSize is 100 or invalid, use a sensible default from interval settings
      let effectiveWindowSize = windowSize;
      if (windowSize >= 100 || windowSize <= 0) {
        // Recalculate from interval settings
        if (fullTimeExtent) {
          const totalDurationMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
          const intervalMs = intervalValue * (
            timeUnit === 'seconds' ? 1000 : 
            timeUnit === 'minutes' ? 60000 : 
            timeUnit === 'hours' ? 3600000 : 86400000
          );
          effectiveWindowSize = Math.min(100, (intervalMs / totalDurationMs) * 100);
        } else {
          effectiveWindowSize = 5; // Fallback: 5%
        }
        windowSizeRef.current = effectiveWindowSize;
      }
      
      setTimeExtent([0, effectiveWindowSize]);
      setShowFullExtent(false);
    }
  }, [showFullExtent, timeExtent, intervalValue, timeUnit, fullTimeExtent]);

  // Format window size for display
  const formatWindowSize = useCallback((): string => {
    if (!fullTimeExtent) return 'N/A';
    const windowSize = timeExtent[1] - timeExtent[0];
    const totalMs = fullTimeExtent.end.getTime() - fullTimeExtent.start.getTime();
    const windowMs = (windowSize / 100) * totalMs;
    
    const seconds = windowMs / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;
    
    if (days >= 1) return `${days.toFixed(1)}d`;
    if (hours >= 1) return `${hours.toFixed(1)}h`;
    if (minutes >= 1) return `${minutes.toFixed(0)}m`;
    return `${seconds.toFixed(0)}s`;
  }, [timeExtent, fullTimeExtent]);

  if (!fullTimeExtent) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Load data to enable timeline playback
        </Typography>
      </Box>
    );
  }
  
  const windowStartTime = getDateFromPercentage(timeExtent[0]);
  const windowEndTime = getDateFromPercentage(timeExtent[1]);

  return (
    <Box sx={{ px: 2, py: 0.75 }}>
      {/* Controls Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Window Size Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={() => adjustWindowSize('narrower')}
            size="small"
            disabled={showFullExtent}
            sx={{ width: 28, height: 28 }}
            title="Narrower window"
          >
            <BackIcon fontSize="small" />
          </IconButton>
          
          <Typography 
            variant="caption" 
            sx={{ 
              minWidth: 35, 
              textAlign: 'center', 
              fontSize: '0.65rem',
              color: 'white',
              fontWeight: 'bold',
              bgcolor: showFullExtent ? theme.palette.success.main : theme.palette.warning.main,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: showFullExtent ? `2px solid ${theme.palette.success.dark}` : `2px solid ${theme.palette.warning.dark}`,
              boxShadow: showFullExtent ? `0 0 8px ${alpha(theme.palette.success.main, 0.5)}` : 'none'
            }}
          >
            {showFullExtent ? 'ALL' : formatWindowSize()}
          </Typography>
          
          <IconButton
            onClick={() => adjustWindowSize('wider')}
            size="small"
            disabled={showFullExtent}
            sx={{ width: 28, height: 28 }}
            title="Wider window"
          >
            <ForwardIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Play/Pause Button */}
        <IconButton
          onClick={() => setIsPlaying(!isPlaying)}
          size="small"
          sx={{ 
            bgcolor: isPlaying ? 'error.light' : 'primary.light',
            '&:hover': { bgcolor: isPlaying ? 'error.main' : 'primary.main' },
            color: 'white',
            width: 32,
            height: 32,
          }}
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
        </IconButton>

        {/* Timeline Range Slider (two handles for visible window) */}
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <Slider
            value={timeExtent}
            onChange={handleSliderChange}
            min={0}
            max={100}
            step={0.1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => {
              const time = getDateFromPercentage(value);
              return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }}
            sx={{
              height: 6,
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
              },
              '& .MuiSlider-track': {
                height: 6,
                border: 'none',
                bgcolor: 'primary.main',
              },
              '& .MuiSlider-rail': {
                height: 6,
                opacity: 0.3,
              },
            }}
          />
        </Box>

        {/* Full Extent Toggle */}
        <IconButton
          onClick={toggleFullExtent}
          size="small"
          sx={{ 
            width: 32, 
            height: 32,
            bgcolor: showFullExtent ? theme.palette.success.main : 'transparent',
            color: showFullExtent ? 'white' : 'inherit',
            border: showFullExtent ? `2px solid ${theme.palette.success.dark}` : 'none',
            boxShadow: showFullExtent ? `0 0 8px ${alpha(theme.palette.success.main, 0.5)}` : 'none',
            '&:hover': {
              bgcolor: showFullExtent ? theme.palette.success.dark : 'rgba(0, 0, 0, 0.04)'
            }
          }}
          title={showFullExtent ? "Show trail mode" : "Show all data"}
        >
          <FullExtentIcon fontSize="small" />
        </IconButton>

        {/* Settings Toggle */}
        <IconButton
          onClick={() => setShowSettings(!showSettings)}
          size="small"
          color={showSettings ? 'primary' : 'default'}
          sx={{ width: 32, height: 32 }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>

        {/* Visibility Toggle */}
        <IconButton
          onClick={(e) => setVisibilityAnchorEl(e.currentTarget)}
          size="small"
          color={visibilityAnchorEl ? 'primary' : 'default'}
          sx={{ width: 32, height: 32 }}
          title="Layer Visibility"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Date Labels Row - Centered below slider */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 1 }}>
        {/* Window Start Time Display */}
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
          {windowStartTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} {windowStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>

        {/* Window End Time Display */}
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
          {windowEndTime.toLocaleDateString([], { month: 'short', day: 'numeric' })} {windowEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>

      {/* Visibility Controls Popover */}
      <Popover
        open={Boolean(visibilityAnchorEl)}
        anchorEl={visibilityAnchorEl}
        onClose={() => setVisibilityAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Layer Visibility
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={showDataPoints} 
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    onShowDataPointsChange?.(newValue);
                    // If turning off data points, also turn off clustering
                    if (!newValue && enableClustering) {
                      onEnableClusteringChange?.(false);
                    }
                  }} 
                  size="small"
                  color="primary"
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                  Data Points ({totalDataPoints > 0 ? totalDataPoints.toLocaleString() : visibleDataPointsCount.toLocaleString()})
                </Typography>
              }
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={showRouteLines} 
                  onChange={(e) => onShowRouteLinesChange?.(e.target.checked)} 
                  size="small"
                  color="success"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Route Lines</Typography>}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={showDirectionArrows} 
                  onChange={(e) => onShowDirectionArrowsChange?.(e.target.checked)} 
                  size="small"
                  color="success"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Direction Arrows</Typography>}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={showHeatmap} 
                  onChange={(e) => onShowHeatmapChange?.(e.target.checked)} 
                  size="small"
                  color="secondary"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Heatmap</Typography>}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={enableClustering} 
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    // If enabling clustering, also enable data points
                    if (newValue && !showDataPoints) {
                      onShowDataPointsChange?.(true);
                    }
                    onEnableClusteringChange?.(newValue);
                  }} 
                  size="small"
                  color="info"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Clustering</Typography>
                  {enableClustering && !showDataPoints && (
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'warning.main', fontStyle: 'italic' }}>
                      (requires data points)
                    </Typography>
                  )}
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={showDwells} 
                  onChange={(e) => onShowDwellsChange?.(e.target.checked)} 
                  size="small"
                  color="warning"
                />
              }
              label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Dwell Locations</Typography>}
            />
          </Box>
        </Paper>
      </Popover>

      {/* Collapsible Settings */}
      <Collapse in={showSettings}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          pt: 1,
          mt: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Unit</InputLabel>
            <Select 
              value={timeUnit} 
              onChange={(e) => setTimeUnit(e.target.value)} 
              label="Unit"
              sx={{ height: 32, fontSize: '0.75rem' }}
            >
              {timeUnits.map(unit => (
                <MenuItem key={unit.value} value={unit.value} sx={{ fontSize: '0.75rem' }}>{unit.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 70 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Interval</InputLabel>
            <Select 
              value={intervalValue} 
              onChange={(e) => setIntervalValue(Number(e.target.value))} 
              label="Interval"
              sx={{ height: 32, fontSize: '0.75rem' }}
            >
              {intervals.slice(0, 30).map(int => (
                <MenuItem key={int} value={int} sx={{ fontSize: '0.75rem' }}>{int}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Play Rate</InputLabel>
            <Select 
              value={playRate} 
              onChange={(e) => setPlayRate(Number(e.target.value))} 
              label="Play Rate"
              sx={{ height: 32, fontSize: '0.75rem' }}
            >
              {playRates.map(rate => (
                <MenuItem key={rate} value={rate} sx={{ fontSize: '0.75rem' }}>{rate}s</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={loop} onChange={(e) => setLoop(e.target.checked)} size="small" />}
            label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Loop</Typography>}
            sx={{ mr: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={followMode} 
                onChange={(e) => handleFollowModeChange(e.target.checked)} 
                size="small" 
                color="secondary"
              />
            }
            label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Follow Mode</Typography>}
            sx={{ mr: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={skipDwells} 
                onChange={(e) => setSkipDwells(e.target.checked)} 
                size="small" 
                color="warning"
              />
            }
            label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Skip Dwells</Typography>}
            sx={{ mr: 0 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default CompactTimeSlider;

